import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as moment from 'moment/moment';
import { MappedReportService } from '../../../../../core/mapped-reports/mapped-report.service';

@Component({
  selector: 'app-mapped-report-list-page',
  templateUrl: './mapped-report-list-page.component.html',
  styleUrls: ['./mapped-report-list-page.component.scss'],
  standalone: false,
})
export class MappedReportListPageComponent implements OnInit, OnDestroy {
  projectId: string;
  rows: any[] = [];
  loading = false;
  selectedRow: any;
  defaultDateValue = 'All time';
  displayedColumns: string[] = ['formNo', 'date', 'inspector', 'bidItems'];
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  form = new FormGroup({
    searchText: new FormControl(''),
  });
  startDate: string | null = null;
  endDate: string | null = null;
  private destroyed = false;
  private templateId: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute, private svc: MappedReportService, private router: Router) {}

  openRow(row: any): void {
    this.selectedRow = row;
    this.router.navigateByUrl(this.svc.buildDeepLinkUrl(this.projectId, row.daily_report_id, row.group_key));
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('projectId') as string;
    // Kick off the fetch asynchronously so the initial render completes
    // before any loading-state transitions take effect.
    Promise.resolve().then(() => this.init());
  }

  onPageChanged(page: number): void {
    this.pagination.pageIndex = page - 1;
    this.fetch();
  }

  search(): void {
    // Search is reactive via the searchText valueChanges subscription.
  }

  close(): void {
    this.form.reset();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async init(): Promise<void> {
    if (this.destroyed) return;
    const templates = await this.svc.getMappedReportTemplates(this.projectId);
    if (this.destroyed) return;
    this.templateId = templates[0]?.id ?? null;
    await this.fetch();

    this.form.controls.searchText.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pagination.pageIndex = 0;
        this.fetch();
      });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(qp => {
      const startDate = qp.startDate && moment(qp.startDate).isValid() ? qp.startDate : null;
      const endDate = qp.endDate && moment(qp.endDate).isValid() ? qp.endDate : null;
      if (startDate !== this.startDate || endDate !== this.endDate) {
        this.startDate = startDate;
        this.endDate = endDate;
        this.pagination.pageIndex = 0;
        this.fetch();
      }
    });
  }

  private async fetch(): Promise<void> {
    if (this.destroyed) return;
    if (!this.projectId || !this.templateId) {
      this.rows = [];
      this.totalRecords = 0;
      return;
    }
    this.loading = true;
    try {
      const resp = await this.svc.listMappedReports({
        projectId: this.projectId,
        templateId: this.templateId,
        search: this.form.controls.searchText.value || undefined,
        from: this.startDate || undefined,
        to: this.endDate || undefined,
        start: this.pagination.pageIndex * this.pagination.pageSize,
        total: this.pagination.pageSize,
      });
      if (this.destroyed) return;
      this.rows = resp.data ?? [];
      this.totalRecords = resp.meta?.totalRecords ?? this.rows.length;
    } finally {
      if (!this.destroyed) this.loading = false;
    }
  }
}
